/**
 * GeoTransform - Transforma coordenadas locales (metros) a GPS y viceversa
 *
 * Asume que el pictograma:
 * - Está en metros
 * - Está alineado al norte (sin rotación)
 *
 * Solo requiere un punto de referencia central.
 */
export class GeoTransform {
  // Metros por grado de latitud (constante aproximada)
  static METERS_PER_DEGREE_LAT = 111320

  /**
   * @param {Object} centerLocal - Punto de referencia en coordenadas locales {x, y} en metros
   * @param {Object} centerGPS - Punto de referencia en GPS {lat, lng}
   */
  constructor(centerLocal, centerGPS) {
    this.centerLocal = centerLocal
    this.centerGPS = centerGPS

    // Metros por grado de longitud (varía según latitud)
    this.metersPerDegreeLng =
      GeoTransform.METERS_PER_DEGREE_LAT * Math.cos((centerGPS.lat * Math.PI) / 180)
  }

  /**
   * Convierte coordenadas locales (metros) a GPS
   * @param {number} x - Coordenada X en metros
   * @param {number} y - Coordenada Y en metros
   * @returns {Object} {lat, lng}
   */
  localToGPS(x, y) {
    return {
      lat: this.centerGPS.lat + (y - this.centerLocal.y) / GeoTransform.METERS_PER_DEGREE_LAT,
      lng: this.centerGPS.lng + (x - this.centerLocal.x) / this.metersPerDegreeLng
    }
  }

  /**
   * Convierte coordenadas GPS a locales (metros)
   * @param {number} lat - Latitud
   * @param {number} lng - Longitud
   * @returns {Object} {x, y} en metros
   */
  gpsToLocal(lat, lng) {
    return {
      x: this.centerLocal.x + (lng - this.centerGPS.lng) * this.metersPerDegreeLng,
      y: this.centerLocal.y + (lat - this.centerGPS.lat) * GeoTransform.METERS_PER_DEGREE_LAT
    }
  }

  /**
   * Obtiene el centro del pictograma en coordenadas GPS
   * @param {Object} bounds - {minX, maxX, minY, maxY} en metros
   * @returns {Object} {lat, lng}
   */
  getCenterGPS(bounds) {
    const centerX = (bounds.minX + bounds.maxX) / 2
    const centerY = (bounds.minY + bounds.maxY) / 2
    return this.localToGPS(centerX, centerY)
  }

  /**
   * Calcula el zoom apropiado para Google Maps basado en el viewport
   * @param {Object} bounds - {minX, maxX, minY, maxY} en metros
   * @param {number} mapWidth - Ancho del contenedor en píxeles
   * @param {number} mapHeight - Alto del contenedor en píxeles
   * @returns {number} Nivel de zoom para Google Maps (1-21)
   */
  getMapZoom(bounds, mapWidth, mapHeight) {
    const cornerA = this.localToGPS(bounds.minX, bounds.minY)
    const cornerB = this.localToGPS(bounds.maxX, bounds.maxY)

    const latDiff = Math.abs(cornerB.lat - cornerA.lat)
    const lngDiff = Math.abs(cornerB.lng - cornerA.lng)

    // Fórmula para calcular zoom de Google Maps
    // Basada en: 360 / 2^zoom = grados visibles horizontalmente a nivel mundial
    const WORLD_DIM = { height: 256, width: 256 }
    const ZOOM_MAX = 21

    function latRad(lat) {
      const sin = Math.sin((lat * Math.PI) / 180)
      const radX2 = Math.log((1 + sin) / (1 - sin)) / 2
      return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2
    }

    function zoom(mapPx, worldPx, fraction) {
      return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2)
    }

    const latFraction = (latRad(cornerB.lat) - latRad(cornerA.lat)) / Math.PI
    const lngFraction = lngDiff / 360

    const latZoom = zoom(mapHeight, WORLD_DIM.height, latFraction)
    const lngZoom = zoom(mapWidth, WORLD_DIM.width, lngFraction)

    return Math.min(latZoom, lngZoom, ZOOM_MAX)
  }

  /**
   * Serializa la configuración para guardar en localStorage
   * @returns {Object}
   */
  toJSON() {
    return {
      centerLocal: this.centerLocal,
      centerGPS: this.centerGPS
    }
  }

  /**
   * Crea una instancia desde datos guardados
   * @param {Object} data - Datos serializados
   * @returns {GeoTransform|null}
   */
  static fromJSON(data) {
    if (!data || !data.centerLocal || !data.centerGPS) {
      return null
    }
    return new GeoTransform(data.centerLocal, data.centerGPS)
  }
}
